import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { X, ArrowSquareOut, CheckCircle, XCircle, Image as ImageIcon } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { StatusBadge } from "../StatusBadge";
import { MARKETPLACES } from "../../platforms";
import { fmtDateTime } from "../../campaignUtils";
import { productImages } from "../../campaignUtils";

function Readiness({ ok, label, testId }) {
  return (
    <div className="flex items-center gap-2 text-sm" data-testid={testId}>
      {ok ? <CheckCircle size={16} weight="fill" className="text-success" /> : <XCircle size={16} weight="fill" className="text-muted-foreground/50" />}
      <span className={cn(ok ? "text-foreground" : "text-muted-foreground")}>{label}</span>
    </div>
  );
}

// In-scope right side panel (no portal). Read-only product details + Promotion Readiness.
export function ProductDetailPanel({ product, onClose }) {
  const { t } = useTranslation();
  const raw = product?.raw || {};
  const images = productImages(product);
  const mkt = product ? MARKETPLACES.find((m) => m.key === product.platform) : null;

  const hasImage = images.length > 0;
  const hasDesc = !!raw.description;
  const hasPrice = product?.price != null;
  const hasLink = !!product?.url;
  const ready = hasImage && hasPrice && hasLink;

  const Field = ({ label, value }) => value == null || value === "" ? null : (
    <div>
      <div className="promo-overline">{label}</div>
      <div className="mt-1 text-sm font-medium">{value}</div>
    </div>
  );

  return (
    <AnimatePresence>
      {product && (
        <div className="fixed inset-0 z-[60]" data-testid="product-detail-panel">
          <motion.div className="absolute inset-0 bg-background/70 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.aside
            className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col border-l border-border bg-card"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "tween", duration: 0.25 }}
          >
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <div className="flex items-center gap-2">
                {mkt && <mkt.Icon size={16} />}
                <span className="promo-overline">{mkt?.label || product.platform}</span>
              </div>
              <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-accent" data-testid="panel-close"><X size={18} /></button>
            </div>

            <div className="flex-1 space-y-5 overflow-y-auto p-5">
              {/* Gallery */}
              {hasImage ? (
                <div className="space-y-2">
                  <div className="aspect-video w-full overflow-hidden rounded-lg border border-border bg-accent/30">
                    <img src={images[0]} alt={product.title} className="h-full w-full object-cover" />
                  </div>
                  {images.length > 1 && (
                    <div className="grid grid-cols-4 gap-2">
                      {images.slice(1, 5).map((im, i) => (
                        <div key={i} className="aspect-square overflow-hidden rounded-md border border-border bg-accent/30">
                          <img src={im} alt="" className="h-full w-full object-cover" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex aspect-video w-full items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground/50">
                  <ImageIcon size={28} weight="duotone" />
                </div>
              )}

              <div>
                <h2 className="text-lg font-medium tracking-tight">{product.title}</h2>
                {hasPrice && <div className="mt-1 text-xl font-semibold tabular-nums">{product.price} {product.currency || ""}</div>}
              </div>

              {hasDesc && <p className="text-sm leading-relaxed text-foreground/90">{raw.description}</p>}

              <div className="grid grid-cols-2 gap-4">
                <Field label={t("promo.catalog.f.category")} value={raw.category} />
                <Field label={t("promo.catalog.f.sku")} value={raw.sku || product.external_id} />
                <div>
                  <div className="promo-overline">{t("promo.catalog.f.status")}</div>
                  <div className="mt-1"><StatusBadge status={product.status === "active" ? "connected" : product.status} label={product.status} /></div>
                </div>
                <Field label={t("promo.catalog.f.lastSync")} value={fmtDateTime(product.updated_at || product.first_synced_at)} />
                {typeof raw.num_favorers === "number" && <Field label={t("promo.catalog.f.favorers")} value={raw.num_favorers} />}
                {images.length > 0 && <Field label={t("promo.catalog.f.images")} value={images.length} />}
              </div>

              {Array.isArray(raw.tags) && raw.tags.length > 0 && (
                <div>
                  <div className="promo-overline mb-2">{t("promo.catalog.f.metadata")}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {raw.tags.map((tag) => (
                      <span key={tag} className="rounded-full border border-border bg-accent/40 px-2 py-0.5 text-xs text-muted-foreground">{tag}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Promotion Readiness */}
              <div className="rounded-lg border border-border bg-accent/20 p-4" data-testid="promotion-readiness">
                <div className="promo-overline mb-3">{t("promo.catalog.readiness.title")}</div>
                <div className="space-y-2">
                  <Readiness ok={hasImage} label={hasImage ? t("promo.catalog.readiness.image") : t("promo.catalog.readiness.imageMissing")} testId="ready-image" />
                  <Readiness ok={hasDesc} label={hasDesc ? t("promo.catalog.readiness.desc") : t("promo.catalog.readiness.descMissing")} testId="ready-desc" />
                  <Readiness ok={hasPrice} label={hasPrice ? t("promo.catalog.readiness.price") : t("promo.catalog.readiness.priceMissing")} testId="ready-price" />
                  <Readiness ok={hasLink} label={hasLink ? t("promo.catalog.readiness.link") : t("promo.catalog.readiness.linkMissing")} testId="ready-link" />
                  <div className="mt-2 border-t border-border/60 pt-2">
                    <Readiness ok={ready} label={ready ? t("promo.catalog.readiness.ready") : t("promo.catalog.readiness.notReady")} testId="ready-overall" />
                  </div>
                </div>
              </div>
            </div>

            {product.url && (
              <div className="border-t border-border p-4">
                <a href={product.url} target="_blank" rel="noreferrer"
                  className="flex items-center justify-center gap-2 rounded-md border border-border px-4 py-2.5 text-sm font-medium transition-colors hover:bg-accent" data-testid="panel-original-link">
                  {t("promo.catalog.viewOriginal")} <ArrowSquareOut size={15} />
                </a>
              </div>
            )}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}
